/**
 * api-fetch/context — shared HTTP plumbing for the fetch KanbanApi.
 *
 * Carries the derived base URLs + the `_request` / `_trelloRequest` /
 * `_casHeaders` closures (auth headers, 401 silent-refresh, network retry,
 * DRF error normalisation, CAS headers) that every domain method factory
 * closes over. `createKanbanFetchApi` (api.fetch.ts) builds one context and
 * threads it into each `create*Api(ctx)` factory. Split out of the former
 * 2010-line api.fetch.ts under the operator 500-line ceiling; behaviour is
 * unchanged.
 */
import type { KanbanAllocationConflict, KanbanCasOpts } from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import { extractDrfErrorCode, formatDrfError } from '../formatDrfError'

export class KanbanAllocationConflictError extends Error {
  conflict: KanbanAllocationConflict
  constructor(conflict: KanbanAllocationConflict, message?: string) {
    super(message || `Email already allocated to ${conflict.board_name} · ${conflict.lane_name}`)
    this.name = 'KanbanAllocationConflictError'
    this.conflict = conflict
  }
}

export interface KanbanFetchApiOptions {
  baseUrl: string
  /** Optional async authorization header resolver (e.g. JWT). */
  getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>
  /** Optional override for the Trello mirror's HTTP base path
   *  (qb!2754 mounted it at `/api/trello/`). When omitted we
   *  derive it from `baseUrl` by replacing the trailing
   *  `/kanban` segment with `/trello` — matches the canonical
   *  Quotech routing where both modules live under `/api/`. */
  trelloApiBaseUrl?: string
  /** Optional override for the Teams Hub HTTP base path
   *  (mounted at `/api/teams/` by the prod backend). Derived by
   *  default from `baseUrl` (`/api/kanban/` → `/api/teams/`) so the
   *  standard Quotech routing where both modules live under `/api/`
   *  works without any extra wiring. */
  teamsApiBaseUrl?: string
  /** Optional callback fired ONCE per failed kanban request, BEFORE
   *  the typed throw. Use it to bridge auth/connectivity signals up
   *  to the host (e.g. dispatch `QUOTECH_AUTH_LOST_EVENT` on 401 so
   *  the global Reconnect banner appears).
   *
   *  Why this is separate from the existing `quotech.kanban.error`
   *  DOM event: the DOM event is a generic toast feed (handled by
   *  ToastProvider) and a kanban-specific channel — a 401 there is
   *  one toast among many. The reconnect banner is a separate,
   *  cross-feature concern owned by `@quotech/tenant-shared`. Mixing
   *  the two channels at the kanban layer would either force a
   *  dependency from `@quotech/ui` → `@quotech/tenant-shared`
   *  (wrong direction in the workspace dep graph) or duplicate the
   *  throttle. This hook is the seam: the kanban package stays
   *  tenant-agnostic; the host wires whichever auth-recovery
   *  channel is appropriate.
   *
   *  Contract: invoked for any non-OK response except 412 (which
   *  has its own typed conflict path). Must not throw; errors are
   *  caught and swallowed so observability never breaks control
   *  flow. */
  onResponseError?: (info: {
    status: number
    method: string
    path: string
    message: string
  }) => void
  /** Optional async hook fired ONLY for 401 responses, BEFORE
   *  `onResponseError`. Wire this to a silent token-refresh helper
   *  (e.g. `attemptSilentRefresh` from `@quotech/tenant-shared`):
   *  if it resolves to `true`, the original request is retried
   *  ONCE with the freshly-minted cookies in place; if it resolves
   *  to `false` (or throws), the request falls through to the
   *  normal error path (`onResponseError` + thrown `Error`).
   *
   *  Why this is a separate hook from `onResponseError`: the
   *  refresh-and-retry contract is conditional on the result. The
   *  existing `onResponseError` is a fire-and-forget observer; we
   *  cannot retrofit a meaningful return value onto it without
   *  breaking every existing caller. A new explicit hook keeps the
   *  contracts independent and lets either be wired without the
   *  other.
   *
   *  Contract: invoked exactly once per request, only on 401. Must
   *  return a promise that resolves to a boolean. Resolving false
   *  or throwing is treated identically (no retry; fall through to
   *  `onResponseError`). */
  onUnauthorized?: () => Promise<boolean>
  /** Optional observer fired once per SUCCESSFUL (2xx) kanban request.
   *  Wire this to a connectivity-recovery signal (e.g.
   *  `dispatchQuotechAuthRestored` from `@quotech/tenant-shared`) so a
   *  sticky "Reconnect Quotech" banner auto-clears the instant a master
   *  call succeeds again — important on a Kanban-only surface where no
   *  email/outlook poll is running to provide that signal otherwise
   *  (BUG-2026-05-31-RECONNECT-BANNER-STICKY).
   *
   *  Contract: invoked after any `res.ok` response, before the typed
   *  return. Must not throw; errors are caught and swallowed so
   *  observability never breaks control flow. */
  onResponseSuccess?: () => void
  /**
   * Opt-in additive priority cold-open adapter (`GET …/progressive-snapshot/`).
   * Default OFF until the backend route is live (BE !4539). Calling a missing
   * route always prints a browser console 404 — soft-fail cannot hide that.
   */
  enableProgressiveSnapshot?: boolean
}

/** Per-request controls that preserve the existing method call signatures. */
export interface KanbanRequestOptions {
  signal?: AbortSignal
  /**
   * HTTP statuses that must not raise `quotech.kanban.error` toasts.
   * Used for additive/optional endpoints (e.g. progressive-snapshot 404
   * while the backend deploy lags the frontend adapter).
   */
  quietStatuses?: number[]
}

function buildUrl(base: string, path: string, query?: Record<string, unknown>): string {
  const cleanBase = base.replace(/\/+$/, '')
  const cleanPath = path.replace(/^\/+/, '')
  let url = `${cleanBase}/${cleanPath}`
  if (query && Object.keys(query).length) {
    const usp = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item !== undefined && item !== null) usp.append(k, String(item))
        }
        continue
      }
      usp.append(k, String(v))
    }
    const q = usp.toString()
    if (q) url += `?${q}`
  }
  return url
}

export interface ApiFetchContext {
  baseUrl: string
  trelloApiBase: string
  teamsApiBase: string
  aiApiBase: string
  reportApiBase: string
  /** FEAT-2026-07-27-KANBAN-CATEGORY-ACCESS — `/api/access/`. */
  accessApiBase: string
  getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>
  buildUrl: (base: string, path: string, query?: Record<string, unknown>) => string
  _request: <T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
    requestOptions?: KanbanRequestOptions,
  ) => Promise<T>
  _trelloRequest: <T>(method: string, path: string, body?: unknown) => Promise<T>
  /** JSON request against any sibling API base (`path` starts with `/`). */
  _siblingRequest: <T>(
    base: string,
    method: string,
    path: string,
    body?: unknown,
  ) => Promise<T>
  _casHeaders: (opts?: KanbanCasOpts) => Record<string, string> | undefined
}

export function createApiFetchContext(opts: KanbanFetchApiOptions): ApiFetchContext {
  const { baseUrl, getAuthHeaders, onResponseError, onUnauthorized, onResponseSuccess } = opts
  // Derive the Trello mirror's base URL from the kanban base when
  // no explicit override was supplied. Both endpoints live under
  // `/api/` in the standard Quotech deployment, so swapping the
  // last path segment is the right default.
  const trelloApiBase =
    opts.trelloApiBaseUrl ?? baseUrl.replace(/\/kanban\/?$/, '/trello')
  // Derive the Teams Hub base URL from the kanban base when no
  // explicit override was supplied. Both endpoints live under
  // `/api/` in the standard Quotech deployment, so swapping the
  // last path segment is the right default.
  const teamsApiBase =
    opts.teamsApiBaseUrl ?? baseUrl.replace(/\/kanban\/?$/, '/teams')
  // Derive the AI base URL (transcription, etc.) the same way. The
  // transcribe endpoint lives at `/api/ai/transcribe/` alongside the
  // other AI endpoints, sibling to `/api/kanban/`.
  const aiApiBase = baseUrl.replace(/\/kanban\/?$/, '/ai')
  // CRM-QUOTE-PIPELINE — the quote→job conversion lives on the canonical
  // report app (`/api/report/quote/{id}/convert/`), sibling to `/api/kanban/`.
  const reportApiBase = baseUrl.replace(/\/kanban\/?$/, '/report')
  // FEAT-2026-07-27-KANBAN-CATEGORY-ACCESS — the platform-wide invite endpoint
  // `/api/access/invite/`, also a sibling. D7: category grants reuse it rather
  // than adding a Kanban-only share route.
  const accessApiBase = baseUrl.replace(/\/kanban\/?$/, '/access')

  // BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM — collapse a burst of
  // IDENTICAL error toasts. Opening an archived/merged card whose lane was
  // soft-deleted 404s on every one of its sub-resource GETs (comments,
  // entries subtree, related, attachments, teams-thread) — each with the
  // SAME "No Card matches the given query" body — and react-query retries
  // each, so the operator saw 7+ stacked identical red toasts. We keep a
  // tiny per-context ring of recently-dispatched `{status|message}` keys and
  // suppress a repeat within a short window, so N identical failures raise
  // ONE toast. Distinct errors (different status or message) are unaffected,
  // and the typed `onResponseError` seam below still fires for every failure
  // (it owns the auth-recovery contract). State is per-context so a fresh
  // `createKanbanFetchApi` (e.g. per test) starts clean.
  const ERROR_TOAST_DEDUPE_MS = 4000
  const recentErrorToasts = new Map<string, number>()
  function shouldDispatchErrorToast(key: string): boolean {
    const now = Date.now()
    for (const [k, ts] of recentErrorToasts) {
      if (now - ts > ERROR_TOAST_DEDUPE_MS) recentErrorToasts.delete(k)
    }
    const last = recentErrorToasts.get(key)
    recentErrorToasts.set(key, now)
    return last === undefined || now - last > ERROR_TOAST_DEDUPE_MS
  }

  async function _doFetch(
    method: string,
    path: string,
    body: unknown,
    query: Record<string, unknown> | undefined,
    extraHeaders: Record<string, string> | undefined,
    requestOptions: KanbanRequestOptions | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }
    if (getAuthHeaders) {
      const extra = await getAuthHeaders()
      Object.assign(headers, extra)
    }
    if (extraHeaders) Object.assign(headers, extraHeaders)
    return fetch(buildUrl(baseUrl, path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal: requestOptions?.signal,
    })
  }

  // Methods we'll transparently retry once on a *network-level*
  // failure (the request never reached the server). GET is always
  // safe; PATCH is the autosave path and is idempotent on the BE
  // (whole-field overwrite), so a lost-in-flight save can be re-sent
  // without duplicating side-effects. POST/DELETE are intentionally
  // NOT auto-retried — they may have side-effects the server could
  // have applied before the connection dropped — the UI offers a
  // manual Retry for those instead.
  const _NETWORK_RETRY_METHODS = new Set(['GET', 'PATCH'])

  /**
   * ``_doFetch`` + bounded network-failure retry. A network failure
   * (offline / DNS / CORS / server-down / a token-refresh host that
   * itself can't be reached) makes the browser's ``fetch()`` reject
   * with ``TypeError: Failed to fetch`` BEFORE any response exists —
   * so it never hits the ``!res.ok`` handling below. Previously that
   * raw ``TypeError`` propagated verbatim and the card drawer showed
   * "Save failed → Failed to fetch" with no recovery. Now we retry
   * idempotent calls once, then rethrow a typed ``KanbanNetworkError``
   * so call-sites can offer Retry / keep the edit / auto-retry on
   * reconnect. HTTP error *responses* are unaffected — they resolve
   * normally and flow through the structured handling.
   */
  async function _doFetchWithRetry(
    method: string,
    path: string,
    body: unknown,
    query: Record<string, unknown> | undefined,
    extraHeaders: Record<string, string> | undefined,
    requestOptions: KanbanRequestOptions | undefined,
  ): Promise<Response> {
    const maxAttempts = _NETWORK_RETRY_METHODS.has(method.toUpperCase())
      ? 2
      : 1
    let lastErr: unknown
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await _doFetch(method, path, body, query, extraHeaders, requestOptions)
      } catch (err) {
        // A deliberate cancellation must propagate untouched.
        if (
          (err instanceof DOMException && err.name === 'AbortError') ||
          (err instanceof Error && err.name === 'AbortError')
        ) {
          throw err
        }
        lastErr = err
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 600 * attempt))
        }
      }
    }
    throw new KanbanNetworkError(undefined, lastErr)
  }

  async function _request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
    requestOptions?: KanbanRequestOptions,
  ): Promise<T> {
    let res = await _doFetchWithRetry(
      method,
      path,
      body,
      query,
      extraHeaders,
      requestOptions,
    )
    // 2026-05-21 — auto silent-refresh on 401. The master Django
    // backend's `quotech_auth` cookie is a 1-hour access cookie; the
    // `quotech_refresh` cookie is 30 days. So a 401 here usually
    // just means "access cookie expired, refresh still valid" — fix
    // it transparently via the host-provided `onUnauthorized` hook
    // (typically wired to `attemptSilentRefresh` in tenant-shared).
    // Retry the original request once. Only fall through to the
    // existing error handling (which fires `QUOTECH_AUTH_LOST_EVENT`
    // and throws) if both the refresh attempt and the retry fail.
    // Without this, the operator gets a "Reconnect Quotech" banner
    // on every routine cookie expiry — broken UX reported 2026-05-21.
    if (res.status === 401 && onUnauthorized) {
      try {
        const refreshed = await onUnauthorized()
        if (refreshed) {
          res = await _doFetchWithRetry(
            method,
            path,
            body,
            query,
            extraHeaders,
            requestOptions,
          )
        }
      } catch { /* swallow — fall through to error handling below */ }
    }
    if (!res.ok) {
      // Arc 5 — 412 carries a structured conflict body that the UI
      // needs (current card snapshot + server timestamp). Surface it
      // as a typed error rather than a flat string so callers can
      // ``catch (e) { if (e instanceof KanbanCardConflictError) … }``
      // without re-parsing the message.
      if (res.status === 412) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- opaque DRF conflict body; fields read defensively below
        let cBody: any = null
        try { cBody = await res.json() } catch { /* ignore */ }
        throw new KanbanCardConflictError(
          (cBody && cBody.detail) || 'Card was updated by another user',
          (cBody && cBody.current) || null,
          (cBody && cBody.current_updated_at) || null,
          (cBody && cBody.expected_updated_at) || null,
        )
      }
      let err = `${method} ${path} failed: ${res.status}`
      // FEAT-2026-05-28-TEAM-ALLOC-HARDENING — capture the
      // structured `code` field (e.g. cross_org / last_owner /
      // invalid_role) so callers can switch on the classifier
      // without parsing English. Attached to the thrown Error
      // below via the ``errorCode`` property.
      let errorCode: string | null = null
      try {
        const j = await res.json()
        const parsed = formatDrfError(j)
        if (parsed) err = parsed
        errorCode = extractDrfErrorCode(j)
      } catch {
        // ignore body parse error
      }
      // BUG-2026-05-20-KANBAN-SILENT-401 — Surface every failed kanban
      // request as a ``quotech.kanban.error`` CustomEvent so the host
      // app can route it to a toast. The kanban package can't depend
      // on a specific toast library (react-hot-toast in the dashboard,
      // sonner in 0x3admin, @quotech/ui ToastProvider in apps/*), so
      // we use the same window-event channel ToastProvider already
      // supports. Without this, the previous code path threw an Error
      // that react-query's useMutation captured into ``mutation.error``
      // — and every call-site relied on the developer remembering to
      // wire an ``onError``. None of the board-list/gallery mutations
      // did, so a 401 (or any 4xx/5xx) silently rejected and the
      // operator saw nothing happen (operator complaint 2026-05-20:
      // "why is right click and archive board not work — it should
      // have archvied"). The root cause was Abhishek's JWT having
      // expired — every kanban GET/PATCH returned 401, and react-
      // query's useMutation.onError was unwired, so the archive
      // mutation rejected without telling anyone. Defence in depth:
      // (1) ALWAYS dispatch, regardless of caller plumbing, so future
      //     surfaces inherit the safety net for free.
      // (2) Wrap dispatch in a try/catch so a missing ``window`` in
      //     SSR/test contexts can't take down a real request.
      try {
        if (
          typeof window !== 'undefined' &&
          typeof CustomEvent !== 'undefined' &&
          !(requestOptions?.quietStatuses ?? []).includes(res.status) &&
          // Dedupe identical toasts (BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM).
          shouldDispatchErrorToast(`${res.status}|${err}`)
        ) {
          window.dispatchEvent(new CustomEvent('quotech.kanban.error', {
            detail: { status: res.status, message: err, method, path },
          }))
        }
      } catch { /* swallow — observability must not break control flow */ }
      // BUG-2026-05-20-MASTER-401-KANBAN-NO-BANNER — bridge non-OK
      // responses up to the host so it can fire whichever auth /
      // connectivity recovery channel it owns (today: the global
      // `QUOTECH_AUTH_LOST_EVENT` consumed by `QuotechReconnectBanner`).
      // Without this, a 401 on `/api/kanban/boards/` produced only a
      // generic kanban toast ("Failed to create the board") with no
      // recovery prompt — the operator saw the same broken UX
      // reported in the original silent-401 incident, just one layer
      // up. The `quotechFetch` path (used by emailApi for
      // `/api/email-accounts/`, `/api/outlook/status/`, etc.) already
      // fires the auth-lost event correctly; this restores parity for
      // the kanban path. Wrapped in try/catch so a misbehaving host
      // hook can never take down a real kanban call.
      if (onResponseError) {
        try {
          onResponseError({ status: res.status, method, path, message: err })
        } catch { /* swallow */ }
      }
      // FEAT-2026-05-28-TEAM-ALLOC-HARDENING — attach the BE's
      // structured ``code`` field (cross_org / last_owner /
      // invalid_role / …) to the thrown Error so callers can
      // switch on the classifier without parsing English. The
      // ``status`` is exposed too so toast styling can vary by
      // 4xx vs 5xx without re-parsing the message.
      const apiError: Error & { errorCode?: string; status?: number } = new Error(err)
      if (errorCode) apiError.errorCode = errorCode
      apiError.status = res.status
      throw apiError
    }
    // Success — announce recovery so a sticky reconnect banner clears
    // on a Kanban-only surface (BUG-2026-05-31-RECONNECT-BANNER-STICKY).
    // Swallowed so a misbehaving host hook can never break a real call.
    if (onResponseSuccess) {
      try {
        onResponseSuccess()
      } catch { /* swallow — observability must not break control flow */ }
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  // Arc 5 — translate ``opts.expectedUpdatedAt`` into the canonical
  // ``If-Unmodified-Since`` header the BE expects (audit §3.6.2 option
  // A). Returns ``undefined`` on missing/null so the legacy code path
  // through ``_request`` is unaffected.
  function _casHeaders(opts?: KanbanCasOpts): Record<string, string> | undefined {
    const ts = opts?.expectedUpdatedAt
    if (!ts) return undefined
    return { 'If-Unmodified-Since': ts }
  }

  // FEAT-2026-06-24-SYNC-DIRECTION — JSON helper for the `/api/trello/`
  // surface (connection settings + card-drift review). The Trello mirror
  // lives at a different base than the kanban `baseUrl`, so it can't go
  // through `_request`. This mirrors the auth-header + credentials +
  // DRF-error handling of the existing inline Trello fetches (e.g.
  // `fetchTrelloCardDetail`) so behaviour is uniform. `path` is relative
  // to `trelloApiBase` and must start with `/`.
  async function _trelloRequest<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    return _siblingRequest<T>(trelloApiBase, method, path, body)
  }

  // FEAT-2026-07-27-KANBAN-CATEGORY-ACCESS — the same JSON helper against ANY
  // sibling API base. Generalised out of `_trelloRequest` (which now calls it)
  // rather than copied a third time: the auth-header / credentials / DRF-error
  // handling below is the part that must not drift between surfaces, and three
  // copies is how one of them quietly stops sending the org header.
  async function _siblingRequest<T>(
    base: string,
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (getAuthHeaders) {
      const extra = await getAuthHeaders()
      Object.assign(headers, extra)
    }
    const cleanBase = base.replace(/\/+$/, '')
    const url = `${cleanBase}${path}`
    const res = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    if (!res.ok) {
      let err = `${method} ${url} failed: ${res.status}`
      try {
        const j = await res.json()
        if (j?.detail) err = j.detail
        else if (j?.error) err = j.error
      } catch {
        // ignore body parse error
      }
      const apiError: Error & { status?: number } = new Error(err)
      apiError.status = res.status
      throw apiError
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  return {
    baseUrl,
    trelloApiBase,
    teamsApiBase,
    aiApiBase,
    reportApiBase,
    accessApiBase,
    getAuthHeaders,
    buildUrl,
    _request,
    _trelloRequest,
    _siblingRequest,
    _casHeaders,
  }
}
